// src/components/staffing/StaffingContainer.jsx
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import StaffingHub from './StaffingHub';
import TeamAssemblyWorkspace from './TeamAssemblyWorkspace';
import staffingService from '../../services/staffingService';
import { Logger } from '../../utils/logger';

const logger = new Logger('StaffingContainer');

/**
 * StaffingContainer manages the routing between Staffing Hub and Team Assembly Workspace
 * Now uses persistent state from useStaffingState hook
 */
const StaffingContainer = ({ onNavigateToView, staffingStateManager }) => {
  // Use persistent state instead of local state
  const {
    staffingState,
    setCurrentView,
    setCurrentStaffing,
    goBackToHub,
    updateStaffingState
  } = staffingStateManager;

  // Extract current values from persistent state
  const currentView = staffingState.currentView;
  const currentStaffingId = staffingState.currentStaffingId;
  const currentOpportunityData = staffingState.currentOpportunityData;

  // Handle starting new staffing
  const handleStartNewStaffing = useCallback(() => {
    // For now, create a mock staffing ID
    // In production, this would call an API to create a new staffing workflow
    const newStaffingId = `new-staffing-${Date.now()}`;
    setCurrentStaffing(newStaffingId);
  }, [setCurrentStaffing]);

  // Handle continuing existing staffing
  const handleContinueStaffing = useCallback((staffingId, opportunityData = null) => {
    logger.debug('Continuing staffing with persistent state:', {
      staffingId,
      hasOpportunityData: !!opportunityData
    });
    setCurrentStaffing(staffingId, opportunityData);
  }, [setCurrentStaffing]);

  // Handle viewing completed staffing
  const handleViewCompleted = useCallback((staffingId) => {
    // For now, just log - in production this would show a read-only view
    logger.debug('View completed staffing:', staffingId);
    // Could navigate to a different view or show a modal
  }, []);

  // Handle importing from opportunity
  const handleImportFromOpportunity = useCallback(() => {
    // For now, just log - in production this would show opportunity selection
    logger.debug('Import from opportunity');
    // Could show a modal to select an opportunity, then create staffing from it
  }, []);

  // Handle going back to hub from workspace
  const handleBackToHub = useCallback(() => {
    logger.debug('Going back to hub, preserving state');
    goBackToHub();
  }, [goBackToHub]);

  // Handle saving staffing data
  const handleSaveStaffing = useCallback(async (staffingData) => {
    try {
      // Update persistent state with staffing data
      updateStaffingState({
        [`staffingData_${staffingData.id}`]: staffingData
      });

      // Convert staffing data to backend format
      const assignments = staffingData.roles
        .filter(role => role.status === 'assigned')
        .map(role => ({
          role_name: role.role_name,
          employee_id: role.assigned_employee_id,
          employee_name: role.assigned_employee_name,
          assigned_at: new Date().toISOString(),
          fit_score: role.fit_score
        }));

      // Save to backend
      await staffingService.saveProgress(staffingData.id, assignments);

      logger.debug('Staffing saved successfully with persistent state:', staffingData.id);
    } catch (error) {
      logger.error('Error saving staffing:', error);
      throw error;
    }
  }, [updateStaffingState]);

  // Handle completing staffing
  const handleCompleteStaffing = useCallback(async (staffingData) => {
    try {
      await staffingService.completeStaffing(staffingData.id);
      logger.debug('Staffing completed:', staffingData.id);

      // Navigate back to hub after completion
      handleBackToHub();
    } catch (error) {
      logger.error('Error completing staffing:', error);
      throw error;
    }
  }, [handleBackToHub]);

  // Render current view
  if (currentView === 'workspace' && currentStaffingId) {
    return (
      <TeamAssemblyWorkspace
        staffingId={currentStaffingId}
        opportunityData={currentOpportunityData}
        staffingStateManager={staffingStateManager}
        onBack={handleBackToHub}
        onSave={handleSaveStaffing}
        onComplete={handleCompleteStaffing}
      />
    );
  }

  return (
    <StaffingHub
      onStartNewStaffing={handleStartNewStaffing}
      onContinueStaffing={handleContinueStaffing}
      onViewCompleted={handleViewCompleted}
      onImportFromOpportunity={handleImportFromOpportunity}
    />
  );
};

StaffingContainer.propTypes = {
  onNavigateToView: PropTypes.func,
  staffingStateManager: PropTypes.object.isRequired
};

export default StaffingContainer;
